import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useHistory } from "react-router-dom";
import { Card, Image, Icon, Loader, Checkbox } from "semantic-ui-react";
import BCButton from "../ui/BCButton";
import BCMessage from "../ui/BCMessage";
import BCCards from "../ui/BCCards";
import BCQuoteIcon from "../../assets/bc-quote-icon.png";
import BCTable from "../ui/BCTable";
import { exportWorkbookFromGmr, getGMRInfoList, updateGmrStatus } from "../../api/BrokerApi";
import { exportMockWorkbookFromGmr, getMockGMRInfolist, updateMockGmrStatus } from "../../api/mock/MockBrokerApi";
import { createFile, guid, handleErrorResponse, isValidPermission } from "../../Helpers";
import "../../styles/landing-page.scss";
import "../../styles/main.scss";
import BCKanban from "../ui/BCKanban";
import { BCFilterBar } from "../BCFilter";
import BCFilterSearch from "../BCFilterSearch";
import BCMenuBar from "../ui/BCMenuBar";
import { useBCGlobalFilter } from "../../helpers/GlobalHelper";
import { GlobalConstants, Personas } from "../constants/GlobalConstants";
import { FeatureFlags } from "../constants/LocalServerConstants";
import { getIsWhiteLabeled, whoAmI } from "../../helpers/PermissionHelper";

const BPPricing = ({ 
  isGreaterThanMobile,
  isGreaterThanTablet,
  isSidebar,
  GMRInfoData,
  setGMRInfoData,
  isMessage,
  setIsMessage,
  errorResponse, 
  setErrorResponse,
  user,
  proxyUser,
  permissions,
  brokerColor,
  setCopiedGmrId,
  setIsPricingStartScenarios
 }) => {
  const [isFirstLoad, setIsFirstLoad] = useState(true as boolean);
  const [isLoading, setIsLoading] = useState(true);
  const [isKanbanView, setIsKanbanView] = useState(localStorage.getItem("infolist") === "table" ? false : true);
  const [isFetching, setIsFetching] = useState(false);
  const [kanbanViews, setKanbanViews] = useState([] as any[]);
  const [currentSliceTo, setCurrentSliceTo] = useState(GlobalConstants.DefaultValues.defaultSliceKanban);
  const [exportWorkbookUrl, setExportWorkbookUrl] = useState(undefined as any);
  const exportWorkbookRef = useRef<HTMLElement>(null);
  const BPPricingKanbanLanes =
  {
    New: {
      name: "Pricing in the Works",
      index: 0
    },
    Sent: {
      name: "Pricing Sent",
      index: 1
    },
    Accepted: {
      name: "Pricing Accepted",
      index: 2
    }
  };

  const persona = whoAmI(user, proxyUser);
  const isInternalUser = [
    Personas.SYSTEM_ADMIN,
    Personas.SALES_ADMIN,
    Personas.BROKER_ADMIN,
    Personas.LOAN_OFFICER,
    Personas.BRM
  ].indexOf(persona) > -1;
  
  const reducerInstance = useBCGlobalFilter(GMRInfoData, { hiddenFilters: ["Closing Date"], filterKeysList: ["loanProgram", "brokerage", "brokerEmail", "name"] });
  const [{ isFilterOpen, getFilteredData, filteredData: filteredGmrData, filterBySearchValue }]: any = reducerInstance;
  const history = useHistory();
  const handleSummary = useCallback((id) => {
    setIsPricingStartScenarios(true);
    history.push(`/getmyrate/${id}`);
  }, [history]);

  const handleError = useCallback(() => {
    history.push("/error");
  }, [history]);

  const handleGMR = useCallback((gmrObj) => {
    setCopiedGmrId(undefined);
    history.push(`/getmyrate/${gmrObj.id ? gmrObj.id : guid()}`);
  }, [history, setCopiedGmrId]);

  const handleCopyGMR = useCallback((gmrObj) => {
    setCopiedGmrId(gmrObj.id);
    history.push(`/getmyrate/${guid()}`);
  }, [history, setCopiedGmrId]);

  const handleDropGmrCard = async (card, i) => {
    const gmrObj = {
      gmrId: card.id,
      status: Object.getOwnPropertyNames(BPPricingKanbanLanes)[i]
    };

    await handleUpdateGmrStatusAsync(gmrObj);
    await getGMRInfoAysnc();
  };

  const getFilteredOrEmptyData = (kanbanData) => {
    if (!kanbanData) {
      return [];
    }

    return getFilteredData(kanbanData.cards);
  };

  const sortFilteredGmrsKanban = () => {
    const kanbanView = [
      {
        index: BPPricingKanbanLanes.New.index, 
        name: BPPricingKanbanLanes.New.name, 
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.New.index], 
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPPricingKanbanLanes.New.index])).slice(0, currentSliceTo)
      }, 
      {
        index: BPPricingKanbanLanes.Sent.index, 
        name: BPPricingKanbanLanes.Sent.name, 
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.Sent.index], 
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPPricingKanbanLanes.Sent.index])).slice(0, currentSliceTo)
      },
      {
        index: BPPricingKanbanLanes.Accepted.index, 
        name: BPPricingKanbanLanes.Accepted.name, 
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.Accepted.index],
        cards: filterBySearchValue(getFilteredOrEmptyData(kanbanViews[BPPricingKanbanLanes.Accepted.index])).slice(0, currentSliceTo)
      }
    ];

    return kanbanView;
  };

  const sortGmrsKanban = (pricingData) => {
    const kanbanView = [
      {
        index: BPPricingKanbanLanes.New.index, 
        name: BPPricingKanbanLanes.New.name, 
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.New.index], 
        cards: [] as any
      }, 
      {
        index: BPPricingKanbanLanes.Sent.index, 
        name: BPPricingKanbanLanes.Sent.name, 
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.Sent.index], 
        cards: [] as any
      },
      {
        index: BPPricingKanbanLanes.Accepted.index, 
        name: BPPricingKanbanLanes.Accepted.name, 
        title: Object.getOwnPropertyNames(BPPricingKanbanLanes)[BPPricingKanbanLanes.Accepted.index], 
        cards: [] as any
      }
    ];

    pricingData.forEach((application) => {
      if (application.statusLookupName && application.name !== null) {
        const kanbanObjIndex = kanbanView.findIndex(x => x.title === application.statusLookupName);
        if (kanbanObjIndex !== -1) {
          kanbanView[kanbanObjIndex].cards.push(application);
        }
      }
    });

    return kanbanView;
  };

  const sortedGmrsKanban = useMemo(() => sortFilteredGmrsKanban(), [filteredGmrData, currentSliceTo]);
  const convertToTableFormat = (gmrs) => {
    const result = [] as any[];
    gmrs.forEach((gmr) => {
      if (gmr.name !== null) {
        result.push({
          "id": gmr.id,
          "Name": gmr.name,
          "Loan Program": gmr.loanProgram,
          "Properties or Units": gmr.numberOfPropertiesOrUnits,
          "Submitted On": gmr.submittedOn ? new Date(gmr.submittedOn).toLocaleDateString() : null
        });
      }
    });

    return result;
  };

  const handleInfolistViewType = () => {
    const infolistStyle = isKanbanView ? "table" : "card";
    localStorage.setItem("infolist", infolistStyle);
    setIsKanbanView(!isKanbanView);
  };

  const handleUpdateGmrStatusAsync = async (gmrObj) => { 
    try {
      const response = FeatureFlags.IS_MOCK ? await updateMockGmrStatus(gmrObj) : await updateGmrStatus(gmrObj, proxyUser.id);
      if (response && response.ok) {
        return true;
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      setIsMessage(false);
      handleError();
    }

    return false;
  };

  const getGMRInfoAysnc = async () => {
    try {
      const response = FeatureFlags.IS_MOCK ? await getMockGMRInfolist() : await getGMRInfoList(proxyUser.id);
      if (response && response.ok) {
        setIsMessage(false);
        const gmrInfoList = await response.json();
        const gmrViews = [] as any[];
        const uniqueInfolist = gmrInfoList.filter((v,i,a)=>a.findIndex(t=>(t.id===v.id))===i);
        uniqueInfolist.forEach((gmrInfoListItem) => {
          const gmrView = {
            id: gmrInfoListItem.id,
            name: gmrInfoListItem.name,
            loanProgram: gmrInfoListItem.loanProgram,
            numberOfPropertiesOrUnits: gmrInfoListItem.numberOfUnitsProperties,
            submittedOn: gmrInfoListItem.submittedOn,
            brokerage: gmrInfoListItem.brokerage,
            brokerEmail: gmrInfoListItem.brokerEmail,
            statusLookupName: gmrInfoListItem.statusLookupName,
            amount: gmrInfoListItem.loanCommitment,
            closingDate: gmrInfoListItem.closingDate,
            assignedTo: gmrInfoListItem.assignedTo,
            channels: gmrInfoListItem.channels
          };

          gmrViews.push(gmrView);
        });

        setGMRInfoData(gmrViews);
        setKanbanViews(sortGmrsKanban(gmrViews));
      }
      else {
        await handleErrorResponse(response, setIsMessage, setErrorResponse);
      }
    }
    catch (error) {
      handleError();
    }

    setIsLoading(false);
  };

  const handleScroll = () => {
    if (window.innerHeight - document.documentElement.scrollTop !== document.documentElement.clientHeight) {
      setIsFetching(true);
    }
    else {
      return;
    }
  };

  const exportWorkbookAsync = async (gmr) => {
    if (gmr.id) {
      setIsLoading(true);
      try {
        const response = FeatureFlags.IS_MOCK ? await exportMockWorkbookFromGmr(gmr.id) : await exportWorkbookFromGmr(gmr.id, proxyUser.id);
        if (response && response.ok) {
          const data = await response.blob();
          const workbook = await createFile(data, "workbook", data.type);
          setExportWorkbookUrl(URL.createObjectURL(workbook));
          if (exportWorkbookRef.current) {
            exportWorkbookRef.current.click();
          }
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        setIsMessage(false);
      }
  
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const getGMRInfoAysnc = async () => {
      try {
        const response = FeatureFlags.IS_MOCK ? await getMockGMRInfolist() : await getGMRInfoList(proxyUser.id) as any;
        if (response && response.status === 204) {
          setIsMessage(false);
        }
        else if (response && response.ok) {
          setIsMessage(false);
          const gmrInfoList = await response.json();
          const gmrViews = [] as any[];
          const uniqueInfolist = gmrInfoList.filter((v,i,a)=>a.findIndex(t=>(t.id===v.id))===i);
          uniqueInfolist.forEach((gmrInfoListItem) => {
            const gmrView = {
              id: gmrInfoListItem.id,
              name: gmrInfoListItem.name,
              loanProgram: gmrInfoListItem.loanProgram,
              numberOfPropertiesOrUnits: gmrInfoListItem.numberOfUnitsProperties,
              submittedOn: gmrInfoListItem.submittedOn,
              brokerage: gmrInfoListItem.brokerage,
              brokerEmail: gmrInfoListItem.brokerEmail,
              statusLookupName: gmrInfoListItem.statusLookupName,
              amount: gmrInfoListItem.loanCommitment,
              closingDate: gmrInfoListItem.closingDate,
              assignedTo: gmrInfoListItem.assignedTo,
              channels: gmrInfoListItem.channels
            };

            gmrViews.push(gmrView);
          });

          setGMRInfoData(gmrViews);
          setKanbanViews(sortGmrsKanban(gmrViews));
        }
        else {
          await handleErrorResponse(response, setIsMessage, setErrorResponse);
        }
      }
      catch (error) {
        handleError();
      }

      setIsLoading(false);
    };

    if (isFirstLoad) {
      getGMRInfoAysnc();
    }
  }, [setIsMessage, setErrorResponse, setIsLoading, setGMRInfoData, handleError, proxyUser, BPPricingKanbanLanes, isFirstLoad, filteredGmrData]);

  useEffect(() => {
    setIsFirstLoad(false);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    if (!isFetching) {
      return;
    }

    setCurrentSliceTo(currentSliceTo + 5);
    setIsFetching(false);
  }, [isFetching]);

  useEffect(() => {
    if (currentSliceTo !== GlobalConstants.DefaultValues.defaultSliceKanban) {
      setCurrentSliceTo(GlobalConstants.DefaultValues.defaultSliceKanban);
    }
  }, [filteredGmrData]);

  return (
    <div className={(isGreaterThanTablet && isSidebar) ? "landing-page hide-sidebar" : "landing-page sidebar"}>
      {<a className="export-link" href={exportWorkbookUrl} download ref={exportWorkbookRef as any}>0</a>}
      {isLoading ? <Loader className="" active inline="centered" size={"big"} /> : <div>
        <Card.Group>
          <BCMessage
            animation={"fly down"}
            duration={750}
            visible={isMessage}
            transitionOnMount
            styles="full-width"
            onDismiss={() => setIsMessage(false)}
            errorResponse={errorResponse}
          />
          <BCMenuBar
            leftChildren={[<React.Fragment>
              {isGreaterThanMobile ?
              <div className="landing-page-button-group">
                {!getIsWhiteLabeled() && <BCButton labelPosition="left" iconName="add" brokerColor={brokerColor} handleOnClick={() => handleGMR({})}>
                  <div>Start Pricing</div>
                </BCButton>}
                {(GMRInfoData && filteredGmrData?.length !== 0) && <Checkbox 
                  className="view-toggle" 
                  toggle 
                  label={{children: isKanbanView ? "Switch to table view!" : "Switch to card view!" }} 
                  onChange={() => handleInfolistViewType()} 
                  defaultChecked={!isKanbanView}
                />}
              </div> : <BCButton brokerColor={brokerColor} handleOnClick={handleGMR}>
                <Icon className="add" fitted />
              </BCButton>
            }</React.Fragment>]}
            rightChildren={[<BCFilterSearch reducerInstance={reducerInstance} hideFilterBar={!isValidPermission(permissions, "GlobalAdminAccess", "read")} />]}
            className={isValidPermission(permissions, "GlobalAdminAccess", "read") && isFilterOpen ? "no-bottom-margin" : ""}
          />
          {isValidPermission(permissions, "GlobalAdminAccess", "read") && <BCFilterBar reducerInstance={reducerInstance}/>}
        </Card.Group>
        {GMRInfoData && filteredGmrData?.length === 0 ?
          <Card.Group>
            <Card fluid>
              <div className="landing-page-empty-container">
                <div className="landing-page-empty-content">
                  {!getIsWhiteLabeled() && <Image className="landing-page-empty-icon center" src={BCQuoteIcon} />}
                  <div className="landing-page-empty-text">Looks like there isn't any pricing requests! Click below and get your loan priced in five minutes or less.</div>
                  <div className="center">
                    <BCButton brokerColor={brokerColor} handleOnClick={() => handleGMR({})}><div>Get Started</div></BCButton>
                  </div>
                </div>
              </div>
            </Card>
          </Card.Group> : isKanbanView === true && isGreaterThanMobile ? 
          <div className="broker-kanban-container">
            <BCKanban
              data={kanbanViews && kanbanViews[0] ? sortedGmrsKanban : []}
              setData={setKanbanViews}
              isGreaterThanMobile={isGreaterThanMobile} 
              infoData={filteredGmrData}
              handleCopyGMR={handleCopyGMR}
              displayList={filteredGmrData} 
              handleGMR={handleGMR}
              handleSummary={(id) => handleSummary(id)}
              ellipsis
              labels={[
                {
                  type: "progress",
                  condition: "submittedOn",
                  text: "In Progress",
                  color: "yellow"
                },
                {
                  type: "completed",
                  completed: true,
                  condition: "submittedOn",
                  text: "Completed",
                  color: "green"
                }
              ]}
              brokerColor={brokerColor}
              handleCustomDropCard={(card, i) => handleDropGmrCard(card, i)}
              ellipsisHandlers={isInternalUser ? [
                {
                  action: (_, item) => exportWorkbookAsync(item),
                  text: "Export Workbook"
                }
              ] : []}
            />
          </div> : isKanbanView === true && isGreaterThanTablet === false ? <React.Fragment>
            <BCCards 
              isGreaterThanMobile={isGreaterThanMobile} 
              infoData={GMRInfoData} 
              handleInfo={handleGMR} 
              displayList={filteredGmrData}
              handleSummary={handleSummary}
              handleGMR={handleGMR}
              ellipsis
              permissions={permissions}
              isKanban={false}
              brokerColor={brokerColor}
              handleCopyGMR={handleCopyGMR}
            />
          </React.Fragment> : isKanbanView === false ? <React.Fragment>
            <BCTable 
              data={convertToTableFormat(filteredGmrData)}
              dataRules={[{ field: "id", rule: "hide" }]}
              handleClick={(index) => GMRInfoData[index] ? handleSummary(GMRInfoData[index].id) : void 0}
              selectable
              handleGMR={handleGMR}
              ellipsis
              brokerColor={brokerColor}
              sortable={["Name", "Loan Program", "Submitted On", "Properties or Units"]}
              pagination={25}
              handleCopyGMR={handleCopyGMR}
              ellipsisHandlers={isInternalUser ? [
                {
                  action: (_, item) => exportWorkbookAsync(item),
                  text: "Export Workbook"
                }
              ] : []}
              editMode
            />
          </React.Fragment> : null
        }
      </div>}
    </div>
  );

};

export default BPPricing;